import { useState } from "react";
import { Copy, Link2Off, Loader2, Mail, MessageCircle, Share2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

export const PackingShare = ({ list }) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [pending, setPending] = useState("");
  const [error, setError] = useState("");
  const [failedAction, setFailedAction] = useState("");
  const [notice, setNotice] = useState("");

  const updateSharing = async (action) => {
    setPending(action);
    setError("");
    setFailedAction("");
    setNotice("");
    if (action === "load") setUrl("");
    try {
      const method = action === "stop" ? "delete" : action === "create" ? "post" : "get";
      const { data } = await api[method](`/packing/${list.id}/share`);
      if (action === "stop") {
        setUrl("");
        setNotice("Sharing stopped. Previously shared links no longer work.");
        setOpen(false);
      } else {
        setUrl(data.token ? new URL(`/shared/packing/${data.token}`, process.env.REACT_APP_BACKEND_URL).href : "");
      }
    } catch {
      setFailedAction(action);
      setError(action === "stop"
        ? "Couldn't confirm that sharing stopped. Please try again."
        : action === "load" ? "Couldn't load the sharing status. Please try again."
        : "Couldn't create the sharing link. Please try again.");
    } finally {
      setPending("");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setNotice("Link copied — paste it into any conversation.");
    } catch {
      setNotice("Select the link above and copy it manually.");
    }
  };

  const shareOnDevice = async () => {
    setNotice("");
    try {
      await navigator.share({ title: list.title, text: `My packing list: ${list.title}`, url });
    } catch (err) {
      if (err.name !== "AbortError") setNotice("Device sharing isn't available here. Use Email, Messages, or Copy link instead.");
    }
  };

  const message = encodeURIComponent(`Here's my packing list, ${list.title}:\n${url}`);
  const smsPrefix = /iPad|iPhone|iPod/.test(navigator.userAgent) ? "sms:&body=" : "sms:?body=";

  return (
    <Dialog open={open} onOpenChange={(next) => {
      if (pending) return;
      setOpen(next);
      if (next) updateSharing("load");
    }}>
      <DialogTrigger asChild>
        <Button data-testid={`share-packing-${list.id}`} variant="outline" size="sm" className="rounded-full font-bold text-stone-700 shrink-0">
          <Share2 className="w-4 h-4 mr-1.5" /> Share
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="packing-share-dialog" className="rounded-3xl font-['Figtree'] w-[calc(100%-2rem)] max-w-lg min-w-0 max-h-[88vh] overflow-y-auto">
        <DialogHeader className="text-left min-w-0 pr-5">
          <DialogTitle data-testid="packing-share-title" className="font-['Nunito'] text-xl break-words">Share your packing list</DialogTitle>
          <DialogDescription data-testid="packing-share-description" className="break-words">
            Anyone with an active link can view “{list.title}” without signing in, but can't edit it.
          </DialogDescription>
        </DialogHeader>
        {pending && <p data-testid="packing-share-loading" role="status" className="flex items-center gap-2 text-sm text-stone-500"><Loader2 className="w-4 h-4 animate-spin" /> {pending === "stop" ? "Stopping sharing…" : pending === "create" ? "Creating your link…" : "Loading sharing status…"}</p>}
        {error && <div className="space-y-2">
          <p data-testid="packing-share-error" role="alert" className="text-sm text-rose-600">{error}</p>
          <Button data-testid="packing-share-retry" variant="outline" onClick={() => updateSharing(failedAction)} disabled={!!pending} className="rounded-full">Try again</Button>
        </div>}
        {!url && !pending && !error && <Button data-testid="packing-share-create" onClick={() => updateSharing("create")} className="rounded-full bg-ac hover:bg-ac-hover text-white font-bold"><Share2 className="w-4 h-4 mr-2" /> Create sharing link</Button>}
        {url && !pending && <div className="space-y-3 min-w-0">
          <div className="flex flex-wrap gap-2 min-w-0">
            <Input id={`packing-share-url-${list.id}`} data-testid="packing-share-url" aria-label="Sharing link" value={url} readOnly onFocus={(e) => e.target.select()} className="min-w-0 flex-1 basis-48 rounded-xl text-sm" />
            <Button data-testid="packing-share-copy" variant="outline" onClick={copyLink} className="rounded-xl font-bold"><Copy className="w-4 h-4 mr-2" /> Copy link</Button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button asChild variant="outline" className="rounded-full font-bold">
              <a data-testid="packing-share-email" href={`mailto:?subject=${encodeURIComponent(`Packing list: ${list.title}`)}&body=${message}`}><Mail className="w-4 h-4 mr-2" /> Email</a>
            </Button>
            <Button asChild variant="outline" className="rounded-full font-bold">
              <a data-testid="packing-share-messages" href={`${smsPrefix}${message}`}><MessageCircle className="w-4 h-4 mr-2" /> Messages</a>
            </Button>
          </div>
          {typeof navigator.share === "function" && <Button data-testid="packing-share-device" onClick={shareOnDevice} className="w-full rounded-full bg-ac hover:bg-ac-hover text-white font-bold"><Share2 className="w-4 h-4 mr-2" /> More sharing options</Button>}
          <Button data-testid="packing-share-stop" onClick={() => updateSharing("stop")} variant="outline" className="w-full rounded-full font-bold text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"><Link2Off className="w-4 h-4 mr-2" /> Stop sharing</Button>
        </div>}
        {notice && <p data-testid="packing-share-notice" role="status" className="text-sm text-ac-softfg">{notice}</p>}
      </DialogContent>
    </Dialog>
  );
};
